import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthContext } from '../../../context/AuthContext'
import { EvalApiService } from '../../services/EvalApiService'
import { EvalModel, ModelType } from '../../types'
import { fmtIso } from '../../utils'
import { DEPLOYMENT_STATUS_STYLES, MODEL_TYPE_COLORS } from '../../constants'

function ModelTypePill({ type }: { type: ModelType }) {
  return (
    <span
      style={{
        ...MODEL_TYPE_COLORS[type],
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 4,
      }}
    >
      {type}
    </span>
  )
}

export const ModelListPage: React.FC = () => {
  const { token } = useAuthContext()
  const [models, setModels] = useState<EvalModel[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [lastSynced, setLastSynced] = useState<Date | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  useEffect(() => {
    EvalApiService.listModels(token ?? '').then(({ items }) => {
      setModels(items)
      setLoading(false)
    })
  }, [token])

  const handleSync = () => {
    if (!window.confirm('This operation is very intensive for MongoDB. Are you sure you want to continue?')) return
    setSyncing(true)
    EvalApiService.syncModels(token ?? '').then(({ items }) => {
      setModels(items)
      setLastSynced(new Date())
      setSyncing(false)
    })
  }

  const filtered = models.filter((m) => {
    if (typeFilter && m.model_type !== typeFilter) return false
    if (statusFilter && m.deployment_status !== statusFilter) return false
    return true
  })

  return (
    <div className="page-content">
      {syncing && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
        }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid rgba(255,255,255,0.2)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Syncing from Ray…</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>This may take up to a minute</div>
        </div>
      )}

      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">Model Registry</div>
          <div className="eval-page-sub">
            {lastSynced ? `Last synced ${fmtIso(lastSynced.toISOString())}` : ''}
          </div>
        </div>
        <button className="eval-btn-ghost" onClick={handleSync} disabled={syncing}>
          Sync from Ray
        </button>
      </div>

      <div className="eval-filters">
        <select
          className="eval-filter-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All types</option>
          <option value="OD">OD</option>
          <option value="PR">PR</option>
          <option value="PC">PC</option>
        </select>
        <select
          className="eval-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="live">Live</option>
          <option value="staging">Staging</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Type</th>
              <th>Deployment Status</th>
              <th>Embeddings</th>
              <th>Registered At</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.model_id}>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                  <Link to={`/eval/models/${m.model_id}`} style={{ color: 'var(--accent)' }}>
                    {m.version}
                  </Link>
                </td>
                <td><ModelTypePill type={m.model_type} /></td>
                <td>
                  <span style={{ ...DEPLOYMENT_STATUS_STYLES[m.deployment_status], fontSize: 12 }}>
                    {m.deployment_status.charAt(0).toUpperCase() + m.deployment_status.slice(1)}
                  </span>
                </td>
                <td>
                  {m.embedding_count != null ? (
                    <span style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      padding: '1px 6px',
                      borderRadius: 4,
                      background: m.embedding_count === 0 ? 'var(--danger)' : 'transparent',
                      color: m.embedding_count === 0 ? '#fff' : 'var(--text-primary)',
                    }}>
                      {m.embedding_count.toLocaleString()}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>—</span>
                  )}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {fmtIso(m.registered_at)}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {m.description ?? '—'}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}>
                  No models match the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
